import styled, { css } from 'styled-components';
import { EntryType } from './entryTypes';

const toneColors: Record<EntryType, { text: string; bg: string }> = {
  revenue: { text: '#1a9e5c', bg: '#e8fff3' },
  expense: { text: '#dc2229', bg: '#fff5f8' },
};

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin: 8px 0 16px;
`;

export const Segmented = styled.div`
  display: inline-flex;
  padding: 3px;
  border-radius: 7px;
  background: #f5f8fa;
  border: 1px solid #eff2f5;
`;

export const SegmentButton = styled.button<{ $active: boolean; $tone: EntryType }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 5px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 500;
  background: transparent;
  color: #5e6278;
  transition: all 0.15s ease;

  .count {
    min-width: 22px;
    padding: 1px 7px;
    border-radius: 10px;
    font-size: 11px;
    background: #eff2f5;
    color: #7e8299;
  }

  ${({ $active, $tone }) =>
    $active &&
    css`
      background: #fff;
      color: ${toneColors[$tone].text};
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

      .count {
        background: ${toneColors[$tone].bg};
        color: ${toneColors[$tone].text};
      }
    `}
`;

export const Search = styled.label`
  position: relative;
  margin: 0;
  width: 280px;
  max-width: 100%;

  svg {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #a1a5b7;
  }

  input {
    width: 100%;
    min-height: 34px;
    padding: 4px 10px 4px 32px;
    font-size: 13px;
    border-radius: 6px;
    border: 1px solid var(--color-input-border, #e4e6ef);
    background: #fff;
    transition: all 0.15s ease;

    &:focus {
      outline: none;
      border-color: var(--color-input-border-focus, #009ef7);
      box-shadow: 0 0 0 3px rgba(0, 158, 247, 0.12);
    }
  }
`;

export const Description = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 0;

  strong {
    font-weight: 500;
    color: #181c32;
  }

  small {
    font-size: 11px;
    color: #7e8299;
  }
`;

export const Actions = styled.div`
  display: flex;
  gap: 4px;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    transition: background 0.15s ease;

    &:hover {
      background: #f5f8fa;
    }
  }
`;

export const Empty = styled.div`
  padding: 32px 16px;
  text-align: center;
  font-size: 13px;
  color: #7e8299;
`;

export const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 17px;
  font-weight: 600;
  color: #181c32;
`;

export const TypeBadge = styled.span<{ $tone: EntryType }>`
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $tone }) => toneColors[$tone].text};
  background: ${({ $tone }) => toneColors[$tone].bg};
`;

export const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Field = styled.label<{ $invalid?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;

  > span.field-label {
    font-size: 12px;
    font-weight: 500;
    color: #5e6278;

    em {
      color: #dc2229;
      font-style: normal;
      margin-left: 2px;
    }
  }

  input,
  textarea {
    width: 100%;
    font-size: 13px;
    padding: 6px 10px;
    min-height: 36px;
    border-radius: 6px;
    border: 1px solid
      ${({ $invalid }) => ($invalid ? '#ef4444' : 'var(--color-input-border, #e4e6ef)')};
    background: #fff;
    color: #181c32;
    transition: all 0.15s ease;

    &::placeholder {
      font-size: 12px;
      color: var(--color-input-placeholder, #a1a5b7);
    }

    &:focus {
      outline: none;
      border-color: ${({ $invalid }) =>
        $invalid ? '#ef4444' : 'var(--color-input-border-focus, #009ef7)'};
      box-shadow: 0 0 0 3px
        ${({ $invalid }) => ($invalid ? 'rgba(239, 68, 68, 0.12)' : 'rgba(0, 158, 247, 0.12)')};
    }
  }

  textarea {
    resize: vertical;
    min-height: 72px;
  }

  > small {
    font-size: 11px;
    color: #dc2229;
  }
`;

export const Switch = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding-left: 0;
  font-size: 13px;
  color: #3f4254;

  .form-check-input {
    margin: 0;
    width: 2.2em;
    height: 1.2em;
    cursor: pointer;
  }

  small {
    font-size: 11px;
    color: #7e8299;
  }
`;
